import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { Contract, ContractSide, ContractWatch, Market, Position } from '../domain/types'
import { MARKET_IDEA_CSV_HEADERS, marketIdeasToCsvRows, toCsv } from '../lib/csv'
import {
  EVENT_RESOLUTION_CHECKLIST,
  bestSide,
  centsToImplied,
  contractEv,
  crossedTarget,
  exitCalculator,
  impliedProbabilitySeries,
  movementAlert,
  multiOutcome,
  noAskCents,
  noBidCents,
  requiredEntryPriceCents,
  resolutionReady,
  spreadCents,
} from '../lib/math'
import { fmtCents, fmtNumber, fmtPct, fmtSigned } from '../lib/format'
import { Sparkline } from '../components/charts'
import { ActionPill } from '../components/widgets'
import { Banner, Button, Card, EmptyState, Field, KV, NumberField, PageHead, Pill, SelectField, TextArea, TextField, Toggle, type Tone } from '../components/ui'

type ContractRow = { market: Market; contract: Contract }

interface CalculatorState {
  contractId: string
  side: ContractSide
  entryPriceCents: number
  contracts: number
  minEdge: number
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function PredictionMarkets() {
  const store = useStore()
  const flatContracts = useMemo(
    () => store.markets.flatMap((market) => market.contracts.map((contract) => ({ market, contract }))),
    [store.markets],
  )
  const watches = materializeWatches(store.markets, store.contractWatches ?? [], store.watchlistContractIds)
  const defaultContract = flatContracts[0]?.contract
  const [calc, setCalc] = useState<CalculatorState>({
    contractId: defaultContract?.id ?? '',
    side: 'yes',
    entryPriceCents: defaultContract?.askCents ?? 50,
    contracts: 10,
    minEdge: 0.02,
  })
  const warnings = buildCorrelationWarnings(store.markets, store.positions)
  const moving = flatContracts.filter(({ contract }) => movementAlert(historyForContract(contract), { lookback: 3 }).significant).length
  const exportMarketIdeas = () => {
    const rows = marketIdeasToCsvRows(store.markets, watches)
    downloadText('edgelab-market-ideas.csv', toCsv(rows, MARKET_IDEA_CSV_HEADERS))
  }

  return (
    <div className="stack">
      <PageHead title="Prediction Markets">
        Kalshi-style yes/no contracts priced 1-99 cents. EdgeLab models bid/ask, liquidity, fees, movement, settlement, and resolution risk for research and paper-trading only.
      </PageHead>

      <Banner tone="info">
        Legal-only research surface. EdgeLab does not place orders, automate execution, bypass venue rules, or verify whether a market is legal for your jurisdiction.
      </Banner>

      {warnings.map((warning) => (
        <Banner key={warning} tone="warn">
          {warning}
        </Banner>
      ))}

      <div className="grid cols-4">
        <Card tight>
          <KV k="Markets" v={store.markets.length} />
          <KV k="Contracts" v={flatContracts.length} />
        </Card>
        <Card tight>
          <KV k="Watched" v={watches.length} />
          <KV k="Target hits" v={countTargetHits(flatContracts, watches)} />
        </Card>
        <Card tight>
          <KV k="Settled" v={flatContracts.filter((row) => row.contract.settlement !== null).length} />
          <KV k="Open" v={flatContracts.filter((row) => row.contract.settlement === null).length} />
        </Card>
        <Card tight>
          <KV k="Movement alerts" v={moving} />
          <KV k="CSV rows" v={flatContracts.length} />
        </Card>
      </div>

      <ResearchTools rows={flatContracts} calc={calc} setCalc={setCalc} />

      <Card title="Market idea export" subtitle="Exports every prediction-market contract and research field as CSV.">
        <div className="btn-row">
          <Button onClick={exportMarketIdeas}>Export all market ideas CSV</Button>
        </div>
      </Card>

      {store.markets.map((market) => (
        <MarketCard key={market.id} market={market} watches={watches} />
      ))}

      <ManualEntry markets={store.markets} />
    </div>
  )
}

function ResearchTools({
  rows,
  calc,
  setCalc,
}: {
  rows: ContractRow[]
  calc: CalculatorState
  setCalc: (next: CalculatorState) => void
}) {
  const selected = rows.find((row) => row.contract.id === calc.contractId) ?? rows[0]
  if (!selected) {
    return (
      <Card title="Contract tools">
        <EmptyState>Add a market contract before using the calculators.</EmptyState>
      </Card>
    )
  }
  const c = selected.contract
  const sideAsk = calc.side === 'yes' ? c.askCents : noAskCents(c.bidCents)
  const sideBid = calc.side === 'yes' ? c.bidCents : noBidCents(c.askCents)
  const ev = c.modelProb === undefined ? null : contractEv({
    side: calc.side,
    modelProb: c.modelProb,
    yesBidCents: c.bidCents,
    yesAskCents: c.askCents,
    contracts: calc.contracts,
    minEdge: calc.minEdge,
  })
  const exit = exitCalculator({
    side: calc.side,
    entryPriceCents: calc.entryPriceCents,
    contracts: calc.contracts,
    yesBidCents: c.bidCents,
    yesAskCents: c.askCents,
  })
  const worthIt = c.modelProb === undefined ? 0 : requiredEntryPriceCents(c.modelProb, calc.side, calc.minEdge)

  return (
    <Card title="Contract tools" subtitle="Exit math and target-entry math use the selected side's current bid/ask and Kalshi-style fees.">
      <div className="grid cols-4">
        <Field label="Contract">
          <select
            value={c.id}
            onChange={(event) => {
              const next = rows.find((row) => row.contract.id === event.target.value)?.contract
              setCalc({
                ...calc,
                contractId: event.target.value,
                entryPriceCents: next ? (calc.side === 'yes' ? next.askCents : noAskCents(next.bidCents)) : calc.entryPriceCents,
              })
            }}
          >
            {rows.map(({ market, contract }) => (
              <option key={contract.id} value={contract.id}>
                {market.title} - {contract.title}
              </option>
            ))}
          </select>
        </Field>
        <SelectField
          label="Side"
          value={calc.side}
          onChange={(side) => setCalc({ ...calc, side, entryPriceCents: side === 'yes' ? c.askCents : noAskCents(c.bidCents) })}
          options={[
            { value: 'yes', label: 'YES' },
            { value: 'no', label: 'NO' },
          ]}
        />
        <NumberField label="Entry paid" suffix="cents" value={calc.entryPriceCents} min={1} max={99} onChange={(entryPriceCents) => setCalc({ ...calc, entryPriceCents })} />
        <NumberField label="Contracts" value={calc.contracts} min={1} max={100000} onChange={(contracts) => setCalc({ ...calc, contracts: Math.max(1, Math.round(contracts)) })} />
      </div>
      <div className="grid cols-4">
        <NumberField label="Min EV per dollar" value={calc.minEdge} min={0} max={1} step={0.01} onChange={(minEdge) => setCalc({ ...calc, minEdge })} />
        <Card tight>
          <KV k="Current side bid/ask" v={`${fmtCents(sideBid)} / ${fmtCents(sideAsk)}`} />
          <KV k="Exit value" v={fmtCents(exit.currentExitValueCents)} />
        </Card>
        <Card tight>
          <KV k="Mark-to-market P/L" v={<span className={exit.markToMarketPnlCents >= 0 ? 'pos' : 'neg'}>{fmtSigned(exit.markToMarketPnlCents, 0)} cents</span>} />
          <KV k="If wrong" v={<span className="neg">{fmtSigned(exit.pnlIfLoseCents, 0)} cents</span>} />
        </Card>
        <Card tight>
          <KV k="Worth-it price" v={worthIt > 0 ? `${calc.side.toUpperCase()} at ${fmtCents(worthIt)} or lower` : 'Set model prob'} />
          <KV k="Fee-adjusted EV" v={ev ? <span className={ev.evCents >= 0 ? 'pos' : 'neg'}>{fmtSigned(ev.evCents, 0)} cents</span> : 'Set model prob'} />
        </Card>
      </div>
    </Card>
  )
}

function MarketCard({ market, watches }: { market: Market; watches: ContractWatch[] }) {
  const setProb = useStore((s) => s.setContractModelProb)
  const settle = useStore((s) => s.settleContract)
  const toggleWatch = useStore((s) => s.toggleWatchContract)
  const upsertWatch = useStore((s) => s.upsertContractWatch)
  const removeWatch = useStore((s) => s.removeContractWatch)
  const setChecklist = useStore((s) => s.setMarketChecklistAnswer)
  const multi = market.mutuallyExclusive
    ? multiOutcome(market.contracts.map((c) => ({ id: c.id, title: c.title, yesCents: c.lastCents })))
    : null
  const checklist = resolutionReady(market.resolutionChecklist ?? {})

  return (
    <Card
      title={market.title}
      subtitle={`${market.category} / ${market.source}${market.correlationGroup ? ` / ${market.correlationGroup}` : ''}`}
      action={<Pill tone={market.mutuallyExclusive ? 'purple' : 'gray'}>{market.mutuallyExclusive ? 'multi-outcome' : 'binary'}</Pill>}
    >
      {multi && (
        <div className="grid cols-4" style={{ marginBottom: 12 }}>
          <Card tight>
            <KV k="YES sum" v={fmtCents(multi.sumYesCents)} />
          </Card>
          <Card tight>
            <KV k="Overround" v={<span className={multi.overroundPct >= 0 ? 'warn' : 'pos'}>{fmtSigned(multi.overroundPct, 1)}%</span>} />
          </Card>
          <Card tight>
            <KV k="Arb flag" v={multi.arbitrage ? <Pill tone="green">possible</Pill> : <Pill tone="gray">none</Pill>} />
          </Card>
          <Card tight>
            <KV k="Resolution" v={market.resolutionDate ?? market.closeTime ?? 'unset'} />
          </Card>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Bid / Ask</th>
              <th>Last</th>
              <th>Spread</th>
              <th>History</th>
              <th>Move</th>
              <th>Your prob</th>
              <th>YES EV/$</th>
              <th>NO EV/$</th>
              <th>Worth it</th>
              <th>Target</th>
              <th>Settlement</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {market.contracts.map((c) => {
              const yesEv = c.modelProb === undefined ? null : contractEv({ side: 'yes', modelProb: c.modelProb, yesBidCents: c.bidCents, yesAskCents: c.askCents })
              const noEv = c.modelProb === undefined ? null : contractEv({ side: 'no', modelProb: c.modelProb, yesBidCents: c.bidCents, yesAskCents: c.askCents })
              const best = c.modelProb === undefined ? null : bestSide(c.modelProb, c.bidCents, c.askCents)
              const worthIt = best && c.modelProb !== undefined ? requiredEntryPriceCents(c.modelProb, best.side, 0.02) : 0
              const watch = watches.find((w) => w.contractId === c.id)
              const watchSideAsk = watch?.side === 'no' ? noAskCents(c.bidCents) : c.askCents
              const targetHit = watch ? crossedTarget(watchSideAsk, watch.targetPriceCents) : false
              const priceHistory = historyForContract(c)
              const alert = movementAlert(priceHistory, { lookback: 3 })
              const history = impliedProbabilitySeries(priceHistory).map((p) => p.prob * 100)
              const settled = c.settlement !== null

              return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                    <div className="help">{c.resolutionSource ?? c.resolutionCriteria ?? 'No source set'}</div>
                  </td>
                  <td className="mono">
                    {fmtCents(c.bidCents)} / {fmtCents(c.askCents)}
                    <div className="help">NO {fmtCents(noBidCents(c.askCents))} / {fmtCents(noAskCents(c.bidCents))}</div>
                  </td>
                  <td className="mono">
                    {fmtCents(c.lastCents)}
                    <div className="help">{fmtPct(centsToImplied(c.lastCents), 0)}</div>
                  </td>
                  <td>
                    <div className="mono">{fmtCents(spreadCents(c.bidCents, c.askCents))}</div>
                    <Pill tone={liquidityTone(c.liquidityScore)}>{fmtPct(c.liquidityScore, 0)} liq</Pill>
                  </td>
                  <td style={{ minWidth: 110 }}>
                    <Sparkline values={history.length ? history : [c.lastCents]} color="auto" height={34} />
                  </td>
                  <td>{alert.significant ? <Pill tone={alert.direction === 'up' ? 'green' : 'red'}>{fmtSigned(alert.changeCents, 0)} cents</Pill> : <Pill tone="gray">stable</Pill>}</td>
                  <td style={{ width: 92 }}>
                    <input
                      className="input mono"
                      type="number"
                      step={0.01}
                      min={0}
                      max={1}
                      value={c.modelProb ?? ''}
                      placeholder="-"
                      onChange={(e) => setProb(c.id, e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                  </td>
                  <td className={`mono ${yesEv ? (yesEv.evPerDollar >= 0 ? 'pos' : 'neg') : 'muted'}`}>{yesEv ? fmtSigned(yesEv.evPerDollar, 3) : '-'}</td>
                  <td className={`mono ${noEv ? (noEv.evPerDollar >= 0 ? 'pos' : 'neg') : 'muted'}`}>{noEv ? fmtSigned(noEv.evPerDollar, 3) : '-'}</td>
                  <td className="mono">{best ? `${best.side.toUpperCase()} <= ${fmtCents(worthIt)}` : '-'}</td>
                  <td style={{ minWidth: 160 }}>
                    {watch ? (
                      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                        <select
                          value={watch.side}
                          onChange={(e) => upsertWatch({ ...watch, side: e.target.value as ContractSide })}
                          title="Watch side"
                          style={{ width: 58 }}
                        >
                          <option value="yes">YES</option>
                          <option value="no">NO</option>
                        </select>
                        <input
                          className="input mono"
                          type="number"
                          min={1}
                          max={99}
                          value={watch.targetPriceCents}
                          onChange={(e) => upsertWatch({ ...watch, targetPriceCents: Number(e.target.value) })}
                          style={{ width: 68 }}
                        />
                        <Button size="sm" variant="ghost" title="Remove watch" onClick={() => removeWatch(c.id, watch.side)}>x</Button>
                        {targetHit && <Pill tone="green">hit</Pill>}
                      </div>
                    ) : (
                      <Button size="sm" onClick={() => toggleWatch(c.id)}>Watch</Button>
                    )}
                  </td>
                  <td>
                    {settled ? (
                      <div>
                        <Pill tone={c.settlement === 'yes' ? 'green' : 'red'}>{c.settlement?.toUpperCase()}</Pill>
                        <div className="help">{c.settledAt?.slice(0, 10) ?? ''}</div>
                      </div>
                    ) : (
                      <div className="btn-row">
                        <Button size="sm" variant="ghost" onClick={() => settle(c.id, 'yes', { source: c.resolutionSource })}>YES</Button>
                        <Button size="sm" variant="ghost" onClick={() => settle(c.id, 'no', { source: c.resolutionSource })}>NO</Button>
                      </div>
                    )}
                  </td>
                  <td>{best ? <ActionPill action={best.decision} /> : <Pill tone="gray">set prob</Pill>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="divider" />
      <div className="row between wrap">
        <div>
          <strong>Event-resolution checklist</strong>
          <div className="help">Ready only when every item is checked against the official settlement source.</div>
        </div>
        <Pill tone={checklist.ready ? 'green' : 'amber'}>{checklist.ready ? 'ready' : `${checklist.missing.length} open`}</Pill>
      </div>
      <div className="grid cols-3" style={{ marginTop: 10 }}>
        {EVENT_RESOLUTION_CHECKLIST.map((item) => (
          <label key={item.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={Boolean(market.resolutionChecklist?.[item.id])}
              onChange={(e) => setChecklist(market.id, item.id, e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
            />
            <span className="help">{item.question}</span>
          </label>
        ))}
      </div>
    </Card>
  )
}

function ManualEntry({ markets }: { markets: Market[] }) {
  const addManualMarket = useStore((s) => s.addManualMarket)
  const addManualContract = useStore((s) => s.addManualContract)
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [marketId, setMarketId] = useState(markets[0]?.id ?? '')
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('Manual')
  const [contractTitle, setContractTitle] = useState('YES')
  const [last, setLast] = useState(50)
  const [bid, setBid] = useState(48)
  const [ask, setAsk] = useState(52)
  const [volume, setVolume] = useState(0)
  const [prob, setProb] = useState(0.5)
  const [correlationGroup, setCorrelationGroup] = useState('')
  const [resolutionSource, setResolutionSource] = useState('')
  const [resolutionCriteria, setResolutionCriteria] = useState('')
  const [mutuallyExclusive, setMutuallyExclusive] = useState(false)

  const disabled = mode === 'new' ? !title.trim() || !contractTitle.trim() : !marketId || !contractTitle.trim()
  const submit = () => {
    const payload = {
      title,
      category,
      contractTitle,
      yesPriceCents: last,
      bidCents: bid,
      askCents: ask,
      lastCents: last,
      volume,
      modelProb: prob,
      correlationGroup: correlationGroup || undefined,
      resolutionSource: resolutionSource || undefined,
      resolutionCriteria: resolutionCriteria || undefined,
      mutuallyExclusive,
    }
    if (mode === 'existing') addManualContract(marketId, payload)
    else addManualMarket(payload)
    setContractTitle('YES')
    setTitle('')
  }

  return (
    <Card title="Manual contract import" subtitle="Add research-only contracts from venues or exported data you are authorized to use.">
      <div className="grid cols-3">
        <SelectField
          label="Import mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'new', label: 'New event' },
            { value: 'existing', label: 'Existing event' },
          ]}
        />
        {mode === 'existing' ? (
          <Field label="Event">
            <select value={marketId} onChange={(e) => setMarketId(e.target.value)}>
              {markets.map((market) => (
                <option key={market.id} value={market.id}>
                  {market.title}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <TextField label="Event / question" value={title} onChange={setTitle} placeholder="Will X happen by date?" />
        )}
        <TextField label="Category" value={category} onChange={setCategory} />
      </div>

      <div className="grid cols-4">
        <TextField label="Contract label" value={contractTitle} onChange={setContractTitle} />
        <NumberField label="Last" suffix="cents" value={last} onChange={setLast} min={1} max={99} />
        <NumberField label="Bid" suffix="cents" value={bid} onChange={setBid} min={1} max={99} />
        <NumberField label="Ask" suffix="cents" value={ask} onChange={setAsk} min={1} max={99} />
      </div>

      <div className="grid cols-4">
        <NumberField label="Volume" value={volume} onChange={setVolume} min={0} />
        <NumberField label="Your probability" value={prob} onChange={setProb} step={0.01} min={0} max={1} />
        <TextField label="Correlation group" value={correlationGroup} onChange={setCorrelationGroup} placeholder="us-rates" />
        <Toggle label="Mutually exclusive event" checked={mutuallyExclusive} onChange={setMutuallyExclusive} />
      </div>

      <div className="grid cols-2">
        <TextField label="Resolution source" value={resolutionSource} onChange={setResolutionSource} placeholder="Official source" />
        <TextArea label="Resolution criteria" value={resolutionCriteria} onChange={setResolutionCriteria} rows={2} placeholder="Exact settlement condition" />
      </div>

      <Button variant="primary" disabled={disabled} onClick={submit}>
        Import contract
      </Button>
    </Card>
  )
}

function buildCorrelationWarnings(markets: Market[], positions: Position[]): string[] {
  const groups = new Map<string, { markets: number; exposure: number }>()
  for (const market of markets) {
    if (!market.correlationGroup) continue
    const current = groups.get(market.correlationGroup) ?? { markets: 0, exposure: 0 }
    groups.set(market.correlationGroup, { ...current, markets: current.markets + 1 })
  }
  for (const position of positions) {
    if (position.kind !== 'prediction' || position.status !== 'open') continue
    const current = groups.get(position.correlationGroup) ?? { markets: 0, exposure: 0 }
    groups.set(position.correlationGroup, { ...current, exposure: current.exposure + Math.abs(position.stake) })
  }
  return [...groups.entries()]
    .filter(([, value]) => value.markets > 1 || value.exposure > 0)
    .map(([group, value]) => `Correlated prediction exposure: ${group} has ${value.markets} tracked markets and ${fmtNumber(value.exposure, 0)} dollars of open paper exposure.`)
}

function countTargetHits(rows: ContractRow[], watches: ContractWatch[]): number {
  let count = 0
  for (const watch of watches) {
    const contract = rows.find((row) => row.contract.id === watch.contractId)?.contract
    if (!contract) continue
    const ask = watch.side === 'yes' ? contract.askCents : noAskCents(contract.bidCents)
    if (crossedTarget(ask, watch.targetPriceCents)) count++
  }
  return count
}

function liquidityTone(score: number): Tone {
  if (score >= 0.75) return 'green'
  if (score >= 0.45) return 'amber'
  return 'red'
}

function historyForContract(contract: Contract): Contract['priceHistory'] {
  const maybe = (contract as { priceHistory?: Contract['priceHistory'] }).priceHistory
  return maybe && maybe.length > 0 ? maybe : [{ time: new Date().toISOString(), yesPriceCents: contract.lastCents }]
}

function materializeWatches(markets: Market[], watches: ContractWatch[], watchIds: string[]): ContractWatch[] {
  const out = [...watches]
  const existingIds = new Set(out.map((watch) => watch.contractId))
  for (const id of watchIds) {
    if (existingIds.has(id)) continue
    const contract = markets.flatMap((market) => market.contracts).find((candidate) => candidate.id === id)
    if (!contract) continue
    out.push({
      contractId: id,
      side: 'yes',
      targetPriceCents: contract.askCents,
      addedAt: '',
      baselineYesCents: contract.lastCents,
    })
  }
  return out
}
